import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AuthRoles, ChargeInformationDto, ChargeInformationService, ChargeType, DataHubStatus } from '@app/api/dmc';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { chargeinformationTable } from '@shared/table-configs/chargeinformation/chargeinformation.table';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import { TranslatePipe } from '@ngx-translate/core';
import { HeaderComponent } from '@shared/components/header/header.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { CardComponent } from '@shared/components/card/card.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TableComponent } from '@shared/components/table/table.component';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import {
  chargeinformationSearchActionMenu
} from '@views/chargeInformation/chargeinformation-search/chargeinformation-search.action-menu';
import {
  ChargeinformationOpenFormComponent
} from '@shared/modals/chargeinformation/chargeinformation-open-form/chargeinformation-open-form.component';
import {
  ChargeinformationCloseComponent
} from '@shared/modals/chargeinformation/chargeinformation-close/chargeinformation-close.component';
import {
  ChargeinformationRequestComponent
} from '@shared/modals/chargeinformation/chargeinformation-request/chargeinformation-request.component';
import { Observable } from 'rxjs';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { SearchStorageService } from '@app/core/services/search-storage.service';
import { SortParam } from '@app/core/constants/sort-param';
import { hasRole } from '@app/core/guards/has-role.guard';

@Component({
  selector: 'app-chargeinformation-search',
  imports: [
    LucideAngularModule,
    ReactiveFormsModule,
    HeaderComponent,
    TranslatePipe,
    ButtonDirective,
    CardComponent,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    TableComponent,
    SelectDirective,
    InputDateDirective
  ],
  templateUrl: './chargeinformation-search.component.html',
  styleUrl: './chargeinformation-search.component.css'
})
export class ChargeinformationSearchComponent implements AfterViewInit {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  public ciWriter = hasRole([AuthRoles.ChargeInformationWriter]);
  public actions: MenuAction[] = [
    {
      ...chargeinformationSearchActionMenu[0],
      callback: () => this.openCreateModal(),
      disabled: () => !this.ciWriter
    },
    {
      ...chargeinformationSearchActionMenu[1],
      callback: () => this.openEditModal(),
      disabled: () => !this.ciWriter || !this.isSingleConfirmedOwnedElementSelected(),
    },
    {
      ...chargeinformationSearchActionMenu[2],
      callback: () => this.openCloseModal(),
      disabled: () => !this.ciWriter || !this.isSingleConfirmedOwnedElementSelected()
    },
    {
      ...chargeinformationSearchActionMenu[3],
      callback: () => this.openRequestInformationsModal(),
      disabled: () => !this.ciWriter
    }
  ]

  public tableData: ChargeInformationDto[] = [];
  public chargeInformationTableConfig: TableConfig<ChargeInformationDto> = {
    ...chargeinformationTable,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.conductSearch(pageSize, page, order),
    contextActions: [
      this.actions[1],
      this.actions[2],
    ]
  };

  public selectedElements: ChargeInformationDto[] = [];

  public queryForm: FormGroup = new FormGroup({
    chargeType: new FormControl(null),
    title: new FormControl(null),
    chargeTypeId: new FormControl(null),
    dataHubStatus: new FormControl(null),
    validFrom: new FormControl(null),
    validTo: new FormControl(null),
    existsInDataHub: new FormControl(null),
    latest: new FormControl(true)
  });

  /** Dropdown Values **/
  public chargeTypeOptions: KeyValue[] = enumToKeyValue(ChargeType, { omitKeys: ['NotSpecified'], prefixValue: 'chargeInformation.chargeTypes.' });
  public dataHubStatusOptions: KeyValue[] = enumToKeyValue(DataHubStatus, { omitKeys: ['NotSpecified'], prefixValue: 'enum.datahub.status.' });
  public existsInDataHubOptions: KeyValue[] = [{ key: 'yes', value: 'common.yes' }, { key: 'no', value: 'common.no' }];

  constructor(
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly chargeInformationService: ChargeInformationService,
    private readonly searchStorageService: SearchStorageService,
  ) {
    this.headerMenuService.setActions(this.actions)
  }

  public conductSearch(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    let query = this.queryForm.value;
    query.existsInDataHub = this.queryForm.get('existsInDataHub')?.value === 'yes';

    if (this.queryForm.get('existsInDataHub')?.pristine) {
      query.existsInDataHub = null;
    }
    else query.existsInDataHub = query.existsInDataHub === 'yes';

    query = removeNullKeys(query);

    return this.chargeInformationService.chargeInformationQueryPost({ ...query, pageSize, page })
  }

  public ngAfterViewInit(): void {
    if (this.searchStorageService.get()) {
      const params = this.searchStorageService.get();
      if (!params) return;

      this.queryForm.patchValue(params);
      this.table?.search();
    }
  }

  private isSingleConfirmedOwnedElementSelected(): boolean {
    return (this.selectedElements.length === 1 &&
      this.selectedElements[0].dataHubStatus === 'Confirmed' &&
      this.selectedElements[0].ownership) as boolean;
  }

  public openCreateModal(): void {
    this.modalService.show({
      component: ChargeinformationOpenFormComponent,
      title: 'chargeInformation.actions.createChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'system.button.create',
          method: 'create'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { currentChargeInformationDto: null }
    }).subscribe(() => {
      this.table?.search()
    })
  }

  public openEditModal(): void {
    this.modalService.show({
      component: ChargeinformationOpenFormComponent,
      title: 'chargeInformation.actions.editChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'system.button.save',
          method: 'saveChanges'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { currentChargeInformationDto: this.selectedElements[0] }
    }).subscribe(() => {
      this.table?.search()
    })
  }

  public openCloseModal(): void {
    this.modalService.show({
      component: ChargeinformationCloseComponent,
      title: 'chargeInformation.actions.shutdownChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'system.button.disband',
          method: 'close'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {chargeInformationDto: this.selectedElements[0]}
    }).subscribe(() => {
      this.table?.search()
    })
  }

  public openRequestInformationsModal(): void {
    this.modalService.show({
      component: ChargeinformationRequestComponent,
      title: 'chargeInformation.actions.requestChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'system.button.request',
          method: 'requestChargeInformations'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ]
    })
  }
}
